import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { castleKeys } from "./castle";

export interface ResearchCost {
  gold: number;
  wood: number;
  stone: number;
  mana: number;
}

export interface ResearchDef {
  id: string;
  name: string;
  description: string;
  cost: ResearchCost;
  durationSeconds: number;
  requiresAcademyLevel: number;
  completed: boolean;
  inProgress: boolean;
  // ISO timestamp when an in-progress research finishes; null otherwise.
  finishesAt: string | null;
}

export interface ResearchListResponse {
  academyLevel: number;
  researches: ResearchDef[];
}

export const researchKeys = {
  list: ["research", "list"] as const,
};

export function useResearches(enabled: boolean = true) {
  return useQuery({
    queryKey: researchKeys.list,
    queryFn: async () => {
      const res = await api.get<ResearchListResponse>("/researches");
      return res.data;
    },
    enabled,
    refetchInterval: 10_000,
  });
}

export function useStartResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (researchId: string) => {
      const res = await api.post("/research/start", { researchId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: researchKeys.list });
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

// Six fixed research branches. Order here is the column order in the grid;
// the catalog ids must start with the prefix listed in `idPrefix` so we can
// split the flat list into per-branch columns.
export interface ResearchBranch {
  id: string;
  name: string;
  // Short Russian description shown under the column header.
  hint: string;
  // Catalog id prefix used to partition the flat list (`logistics-1`,
  // `logistics-2`, ... → branch "logistics").
  idPrefix: string;
}

export const ResearchBranches: ResearchBranch[] = [
  { id: "economy",      name: "Экономика",  hint: "Производство",       idPrefix: "economy-" },
  { id: "logistics",    name: "Логистика",  hint: "Лимит марша",        idPrefix: "logistics-" },
  { id: "march-speed",  name: "Гонцы",      hint: "Скорость марша",     idPrefix: "march-speed-" },
  { id: "build-speed",  name: "Строители",  hint: "Скорость постройки", idPrefix: "build-speed-" },
  { id: "train-speed",  name: "Дисциплина", hint: "Скорость найма",     idPrefix: "train-speed-" },
  { id: "tactics",      name: "Тактика",    hint: "Атака в бою",        idPrefix: "tactics-" },
];
